/**
 * "Mis pacientes" del terapeuta: pacientes creados por él (sin padre) + niños de
 * padres con vínculo activo, distinguibles. Alta de paciente propio y acceso
 * directo a "Evaluar ahora" y a la ficha (historial + detalle clínico).
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { childrenApi } from "../../api/endpoints.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Badge } from "../../components/Badge.js";
import { EmptyState } from "../../components/EmptyState.js";
import { Loader } from "../../components/Loader.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useAuth } from "../../auth/AuthContext.js";
import { edadEnAnios } from "../../utils/edad.js";
import type { Nino } from "../../api/types.js";
import s from "./TherapistPatientsPage.module.css";

/**
 * Banner de MIGRACIÓN: si el terapeuta tiene pacientes propios "a medias" (sin
 * consentimiento de audio de flujos previos), DECLARA su autorización en lote.
 * En el modelo con la declaración en el alta, hoy no debería mostrarse a nadie.
 */
function BannerConsentimiento({ onDeclarado }: { onDeclarado: () => void }) {
  const q = useQuery(() => childrenApi.consentPendientes());
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const total = q.data?.total ?? 0;
  if (total === 0) return null;

  async function confirmar() {
    setError(null);
    setOcupado(true);
    try {
      await childrenApi.declararConsentimientoLote();
      q.reload();
      onDeclarado();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <InfoBanner tone="warn">
      <div className={s.bannerBody}>
        <span>
          Confirma la autorización del tutor de tus <strong>{total}</strong> paciente
          {total === 1 ? "" : "s"} sin registro. Declaras que cuentas con ella para evaluar y
          procesar su voz.
        </span>
        {error ? <ErrorBanner message={error} /> : null}
        <div>
          <Button variant="sun" size="sm" disabled={ocupado} onClick={() => void confirmar()}>
            {ocupado ? "Confirmando…" : "Confirmar"}
          </Button>
        </div>
      </div>
    </InfoBanner>
  );
}

function PacienteRow({ n, propio }: { n: Nino; propio: boolean }) {
  const navigate = useNavigate();
  return (
    <Card className={s.patient}>
      <div className={s.patientInfo}>
        <div className={s.patientName}>
          <strong>{n.nombre}</strong>
          <Badge tone={propio ? "teal" : "neutral"}>{propio ? "Propio" : "Vinculado"}</Badge>
        </div>
        <div className={s.patientSub}>
          {edadEnAnios(n.fechaNacimiento)} años{propio ? ` · ${n.sexo ?? "—"}` : ""}
        </div>
      </div>
      <div className={s.patientActions}>
        <Button variant="primary" size="sm" onClick={() => navigate(`/terapeuta/paciente/${n.id}/evaluar`)}>
          Evaluar
        </Button>
        <Link to={`/terapeuta/paciente/${n.id}`}>
          <Button variant="secondary" size="sm">
            Ficha
          </Button>
        </Link>
      </div>
    </Card>
  );
}

export function TherapistPatientsPage() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { data: ninos, loading, error, reload } = useQuery(() => childrenApi.listar());

  const propios = (ninos ?? []).filter((n) => n.padreId === null && n.createdBy === usuario?.id);
  const vinculados = (ninos ?? []).filter((n) => n.padreId !== null);
  const vacio = ninos && propios.length === 0 && vinculados.length === 0;

  return (
    <>
      <ScreenHeader title="Mis pacientes" />
      <Content>
        <BannerConsentimiento onDeclarado={reload} />
        <Button variant="primary" block onClick={() => navigate("/terapeuta/paciente/nuevo")}>
          ＋ Nuevo paciente
        </Button>

        {loading ? <Loader /> : null}
        {error ? (
          <>
            <ErrorBanner message={error} />
            <Button variant="ghost" onClick={reload}>
              Reintentar
            </Button>
          </>
        ) : null}

        {vacio ? (
          <EmptyState
            icon="🧒"
            title="Aún no tienes pacientes"
            description="Crea tu primer paciente o vincula un niño de un padre para empezar a evaluar."
          />
        ) : null}

        {propios.length > 0 ? (
          <section className={s.group}>
            <h2 className={s.groupTitle}>Pacientes propios</h2>
            {propios.map((n) => (
              <PacienteRow key={n.id} n={n} propio />
            ))}
          </section>
        ) : null}

        {vinculados.length > 0 ? (
          <section className={s.group}>
            <h2 className={s.groupTitle}>Niños vinculados (de padres)</h2>
            {vinculados.map((n) => (
              <PacienteRow key={n.id} n={n} propio={false} />
            ))}
          </section>
        ) : null}
      </Content>
    </>
  );
}
